import React, { useState, useEffect } from "react";
import CThemeProvider from "../../../basecomp/newComponents/CThemeProvider";
import CBox from "../../../basecomp/newComponents/CBox";
import CText from "../../../basecomp/newComponents/CText";
import CTable from "../../../basecomp/newComponents/CTable";
import CButton from "../../../basecomp/newComponents/CButton";
import CCard from "../../../basecomp/newComponents/CCard";
import CInput from "../../../basecomp/newComponents/CInput";
import CTooltip from "../../../basecomp/newComponents/CTooltip";
import CChip from "../../../basecomp/newComponents/CChip";
import CBackDrop from "../../../basecomp/components/CBackDrop";
import { conasTheme } from "../../../basecomp/newComponents/CTheme";
import EditMaterialDialog from "./EditableModal";
import DataDetailVM from "../viewmodel/DataDetailVM";
import { getDetailData } from "../model/DataProcessingModel.jsx";

// MUI Components
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

// Icons
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import RefreshIcon from "@mui/icons-material/Refresh";
import LinkIcon from "@mui/icons-material/Link";

const EnrichmentDetailTable = () => {
    const {
        selectedDetailData,
        projectName,
        customerName,
        handleBack,
        setSelectedDetailData,
    } = DataDetailVM();

    const [detailData, setDetailData] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [editData, setEditData] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [dimensionsArray, setDimensionsArray] = useState([]);
    const [dimensionValues, setDimensionValues] = useState({});
    const [statusFilter, setStatusFilter] = useState('');
    const [commodityFilter, setCommodityFilter] = useState('');
    const [filtersApplied, setFiltersApplied] = useState(false);
    const [statusOptions, setStatusOptions] = useState([]);
    const [commodityOptions, setCommodityOptions] = useState([]);
    // api name changed
    // ✅ Add backdrop loading state
    const [backdropOpen, setBackdropOpen] = useState(false);

    // Calculate the maximum chip width based on all possible status values
    const getMaxChipWidth = React.useMemo(() => {
        if (!detailData || detailData.length === 0) return 'auto';

        const allStatusValues = detailData.map(record => record["Status"] || '').filter(Boolean);

        if (allStatusValues.length === 0) return 'auto';

        // Find the longest status label
        const longestStatus = allStatusValues.reduce((longest, current) =>
            String(current).length > String(longest).length ? current : longest, ''
        );

        // Calculate approximate width (you can adjust the multiplier as needed)
        // Each character is roughly 8-10px wide, plus padding for chips
        const estimatedWidth = Math.max(String(longestStatus).length * 8 + 40, 100); // minimum 100px for chips
        return `${estimatedWidth}px`;
    }, [detailData]);

    useEffect(() => {
        if (!selectedDetailData?.records?.length) return;

        setIsLoading(true);

        const data = selectedDetailData.records;

        // ✅ Only update detailData on initial load or when data count changes significantly
        // This prevents reordering after edit operations
        if (detailData.length === 0 || Math.abs(data.length - detailData.length) > 5) {
            setDetailData(data);
        } else {
            // If it's a refresh after edit, update existing rows with fresh data while preserving order
            const currentOrderIds = detailData.map(row => row.id);
            const freshDataMap = new Map();
            data.forEach(record => {
                freshDataMap.set(record.id, record);
            });

            // Update existing rows with fresh data, maintaining current order
            const updatedData = detailData.map(currentRow => {
                const freshRow = freshDataMap.get(currentRow.id);
                return freshRow || currentRow; // Use fresh data if available, otherwise keep current
            });

            setDetailData(updatedData);
        }

        // Extract unique statuses and commodities from records
        const uniqueStatuses = [...new Set(
            data.map(item => item["Status"])
                .filter(v => v !== null && v !== undefined && v !== "")
        )];

        const uniqueCommodities = [...new Set(
            data.map(item => item["Commodity"])
                .filter(v => v !== null && v !== undefined && v !== "")
        )];

        // Create properly sanitized dropdown options
        const statusDropdownOptions = uniqueStatuses.map((status, index) => {
            let safeStatus = "";

            if (status !== null && status !== undefined) {
                safeStatus = typeof status === 'string' ? status : String(status);
            }

            return {
                id: index + 1,
                name: safeStatus,
                value: safeStatus,
                label: safeStatus
            };
        });

        const commodityDropdownOptions = uniqueCommodities.map((commodity, index) => {
            let safeCommodity = "";

            if (commodity !== null && commodity !== undefined) {
                safeCommodity = typeof commodity === 'string' ? commodity : String(commodity);
            }

            return {
                id: index + 1,
                name: safeCommodity,
                value: safeCommodity,
                label: safeCommodity
            };
        });

        const validStatusOptions = statusDropdownOptions.filter(opt =>
            typeof opt.value === 'string' || typeof opt.value === 'number'
        );

        const validCommodityOptions = commodityDropdownOptions.filter(opt =>
            typeof opt.value === 'string' || typeof opt.value === 'number'
        );

        setStatusOptions(validStatusOptions);
        setCommodityOptions(validCommodityOptions);

        setIsLoading(false);
    }, [selectedDetailData]);

    // Track when filters are applied
    useEffect(() => {
        const filtersActive = searchTerm !== '' || statusFilter !== '' || commodityFilter !== '';
        setFiltersApplied(filtersActive);
    }, [searchTerm, statusFilter, commodityFilter]);

    // Search input handlers
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setPage(0);
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        setPage(0);
    };

    // Status filter handler - updated for Autocomplete
    const handleStatusFilterChange = (event, newValue) => {
        const statusValue = newValue ? newValue.value : '';
        setStatusFilter(statusValue);
        setPage(0);
    };

    // Commodity filter handler - updated for Autocomplete
    const handleCommodityFilterChange = (event, newValue) => {
        const commodityValue = newValue ? newValue.value : '';
        setCommodityFilter(commodityValue);
        setPage(0);
    };

    // Reset all filters
    const handleResetFilters = () => {
        setSearchTerm('');
        setStatusFilter('');
        setCommodityFilter('');
        setPage(0);
    };

    // Table pagination handlers
    const handleChangePage = (_, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // ✅ Edit row handlers with backdrop
 // ✅ Edit row handlers with backdrop and reference links support
const handleEditClick = (row) => {
    // ✅ Show backdrop when opening modal
    setBackdropOpen(true);

    let dims = [];
    let dimValues = {};

    // Create a properly formatted editData object with mapped fields
    const formattedEditData = {
        id: row.id || row["id"],
        material_number: row["Material Number Index"],
        material_description: row["Material Description"],
        material_description_2: row["Material Description 2"],
        material_description_3: row["Material Description 3"],
        manufacturer_name_1: row["Manufacturer Name 1"],
        manufacturer_part_number_1: row["Manufacturer Part Number 1"],
        manufacturer_name_2: row["Manufacturer Name 2"],
        manufacturer_part_number_2: row["Manufacturer Part Number 2"],
        manufacturer_name_3: row["Manufacturer Name 3"],
        manufacturer_part_number_3: row["Manufacturer Part Number 3"],
        commodity: row["Commodity"],
        commodity_2: row["Commodity 2"],
        standard_name: row["Standard Name"],
        standard_name_2: row["Standard Name 2"],
        type: row["Type"],
        uom: row["UOM"],
        status: row["Status"],
        special_features: row["Special Features"],
        qc_comments: row["QC Comments"],
        comments: row["Comments"],
        general_comments: row["General Comments"],
        long_description: row["Long Description"],
        // ✅ ADD REFERENCE LINKS SUPPORT
        reference_links: row["Reference Links"] || row["reference_links"] || [],
        // Include all original properties to ensure nothing is missed
        ...row
    };

    if (row.dimensions) {
        if (Array.isArray(row.dimensions)) {
            dims = [...row.dimensions];
        } else if (typeof row.dimensions === 'string') {
            try {
                dims = JSON.parse(row.dimensions);
            } catch (e) {
                dims = row.dimensions.split(',').map(item => item.trim());
            }
        }

        dims.forEach(dim => {
            const value =
                row[dim] ||
                row[dim.replace(' ', '_')] ||
                row[dim.replace(/\s+/g, '_')] ||
                row[dim.toLowerCase()] ||
                row[dim.toLowerCase().replace(' ', '_')] ||
                '';

            dimValues[dim] = value;
            formattedEditData[dim.replace(' ', '_')] = value;
        });
    }

    setDimensionsArray(dims);
    setDimensionValues(dimValues);
    setEditData(formattedEditData);

    // ✅ Small delay to show backdrop before opening modal
    setTimeout(() => {
        setModalOpen(true);
        setBackdropOpen(false);
    }, 300);
};

    // ✅ FIXED: Update row in same position without changing order
    const handleSaveClick = async (saveSuccess = false, batchId) => {
        if (editData.id) {
            // ✅ Show backdrop when saving
            setBackdropOpen(true);

            const updatedEditData = {
                ...editData,
                dimensions: dimensionsArray
            };

            // ✅ Update the row in the same position using map
            setDetailData(prevDetailData =>
                prevDetailData.map(row => {
                    if (row.id === updatedEditData.id) {
                        // Merge the updated data with the original row, preserving the original structure
                        return {
                            ...row, // Keep original row structure and fields
                            // Update the display fields with the new values
                            "Material Number Index": updatedEditData.material_number || row["Material Number Index"],
                            "Material Description": updatedEditData.material_description || row["Material Description"],
                            "Material Description 2": updatedEditData.material_description_2 || row["Material Description 2"],
                            "Material Description 3": updatedEditData.material_description_3 || row["Material Description 3"],
                            "Manufacturer Name 1": updatedEditData.manufacturer_name_1 || row["Manufacturer Name 1"],
                            "Manufacturer Part Number 1": updatedEditData.manufacturer_part_number_1 || row["Manufacturer Part Number 1"],
                            "Manufacturer Name 2": updatedEditData.manufacturer_name_2 || row["Manufacturer Name 2"],
                            "Manufacturer Part Number 2": updatedEditData.manufacturer_part_number_2 || row["Manufacturer Part Number 2"],
                            "Manufacturer Name 3": updatedEditData.manufacturer_name_3 || row["Manufacturer Name 3"],
                            "Manufacturer Part Number 3": updatedEditData.manufacturer_part_number_3 || row["Manufacturer Part Number 3"],
                            "Commodity": updatedEditData.commodity || row["Commodity"],
                            "Commodity 2": updatedEditData.commodity_2 || row["Commodity 2"],
                            "Standard Name": updatedEditData.standard_name || row["Standard Name"],
                            "Standard Name 2": updatedEditData.standard_name_2 || row["Standard Name 2"],
                            "Type": updatedEditData.type || row["Type"],
                            "UOM": updatedEditData.uom || row["UOM"],
                            "Status": updatedEditData.status || row["Status"],
                            "Special Features": updatedEditData.special_features || row["Special Features"],
                            "QC Comments": updatedEditData.qc_comments || row["QC Comments"],
                            "Comments": updatedEditData.comments || row["Comments"],
                            "General Comments": updatedEditData.general_comments || row["General Comments"],
                            "Long Description": updatedEditData.long_description || row["Long Description"],
                            // Also update any dimension fields
                            ...dimensionsArray.reduce((acc, dim) => {
                                const value = dimensionValues[dim];
                                if (value !== undefined) {
                                    acc[dim] = value;
                                    acc[dim.replace(/\s+/g, '_')] = value;
                                }
                                return acc;
                            }, {})
                        };
                    }
                    return row; // Return unchanged row if not the edited one
                })
            );

            if (saveSuccess) {
                try {
                    // ✅ Refresh data from server while preserving current display order
                    const response = await getDetailData(batchId);
                    if (response && response.records) {
                        // Update the parent component's data - the useEffect will handle merging intelligently
                        setSelectedDetailData(response);
                    }
                } catch (error) {
                    console.error("Error refreshing data:", error);
                }
            }

            // ✅ Close modal and hide backdrop
            setEditData({});
            setDimensionsArray([]);
            setDimensionValues({});

            // ✅ Hide backdrop after a short delay
            setTimeout(() => {
                setBackdropOpen(false);
            }, 300);
        }
    };

    // ✅ Cancel click handler with backdrop
    const handleCancelClick = () => {
        // ✅ Show backdrop when closing modal
        setBackdropOpen(true);

        setModalOpen(false);
        setEditData({});
        setDimensionsArray([]);
        setDimensionValues({});

        // ✅ Hide backdrop after a short delay
        setTimeout(() => {
            setBackdropOpen(false);
        }, 300);
    };

    const handleFieldChange = (field, value) => {
        setEditData(prevData => ({
            ...prevData,
            [field]: value,
            // Also update the corresponding original field name if it exists
            ...(prevData[`${field.charAt(0).toUpperCase() + field.slice(1)}`] !== undefined
                ? { [`${field.charAt(0).toUpperCase() + field.slice(1)}`]: value }
                : {})
        }));
    };

    const handleDimensionFieldChange = (dimensionName, value) => {
        // Update dimensionValues
        const updatedDimensionValues = {
            ...dimensionValues,
            [dimensionName]: value
        };

        setDimensionValues(updatedDimensionValues);

        // Update both field naming conventions in editData for robustness
        const fieldNameWithUnderscore = dimensionName.replace(/\s+/g, '_');
        handleFieldChange(fieldNameWithUnderscore, value);

        // Also update camelCase version if needed
        const camelCaseField = dimensionName.replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) =>
            index === 0 ? word.toLowerCase() : word.toUpperCase()
        ).replace(/\s+/g, '');

        if (camelCaseField !== fieldNameWithUnderscore) {
            handleFieldChange(camelCaseField, value);
        }
    };

    // Update long description when relevant fields change
    useEffect(() => {
        if (!editData.id) return;

        const { standard_name, standard_name_2, type } = editData;

        const generatedLongDesc = [standard_name, standard_name_2, type]
            .filter(Boolean)
            .join(' - ');

        handleFieldChange('long_description', generatedLongDesc);
    }, [editData.standard_name, editData.standard_name_2, editData.type]);

    // Replace the existing filteredData logic with this comprehensive search:

    const filteredData = detailData.filter(row => {
        const matchesSearch = !searchTerm ? true : (() => {
            // Get original search term (preserve user input as-is)
            const originalSearchTerm = searchTerm;

            if (!originalSearchTerm) return true;

            // Create multiple search variations
            const searchVariations = [
                originalSearchTerm,                    // Original: "CO-01E006-00010"
                originalSearchTerm.toLowerCase(),      // Lowercase: "co-01e006-00010"
                originalSearchTerm.toUpperCase(),      // Uppercase: "CO-01E006-00010"
                originalSearchTerm.trim(),             // Trimmed: "CO-01E006-00010"
                originalSearchTerm.trim().toLowerCase(), // Trimmed + lowercase: "co-01e006-00010"
                originalSearchTerm.trim().toUpperCase()  // Trimmed + uppercase: "CO-01E006-00010"
            ];

            // Helper function to safely get field values in multiple formats
            const getFieldVariations = (value) => {
                if (value === null || value === undefined) return [];

                const stringValue = String(value);
                return [
                    stringValue,                    // Original
                    stringValue.toLowerCase(),      // Lowercase
                    stringValue.toUpperCase(),      // Uppercase
                    stringValue.trim(),             // Trimmed
                    stringValue.trim().toLowerCase(), // Trimmed + lowercase
                    stringValue.trim().toUpperCase()  // Trimmed + uppercase
                ];
            };

            // All searchable fields
            const searchFields = [
                row["Material Number Index"],
                row["material_number_index"], // Alternative field name
                row["Material Description"],
                row["Material Description 2"],
                row["Material Description 3"],
                row["Manufacturer Name 1"],
                row["Manufacturer Name 2"],
                row["Manufacturer Name 3"],
                row["Manufacturer Part Number 1"],
                row["Manufacturer Part Number 2"],
                row["Manufacturer Part Number 3"],
                row["Commodity"],
                row["Commodity 2"],
                row["Status"],
                row["Standard Name"],
                row["Standard Name 2"],
                row["Type"],
                row["Special Features"],
                row["Comments"],
                row["QC Comments"]
            ];

            // Check if any search variation matches any field variation
            return searchFields.some(fieldValue => {
                const fieldVariations = getFieldVariations(fieldValue);

                return searchVariations.some(searchVar =>
                    fieldVariations.some(fieldVar =>
                        fieldVar.includes(searchVar)
                    )
                );
            });
        })();

        // Status filter with comprehensive string comparison
        const matchesStatus = !statusFilter ? true : (() => {
            const rowStatus = row["Status"];
            if (rowStatus === null || rowStatus === undefined) return false;

            const statusString = String(rowStatus);
            const filterString = String(statusFilter);

            // Try multiple comparison formats
            return (
                statusString === filterString ||
                statusString.trim() === filterString.trim() ||
                statusString.toLowerCase() === filterString.toLowerCase() ||
                statusString.trim().toLowerCase() === filterString.trim().toLowerCase()
            );
        })();

        // Commodity filter with comprehensive string comparison
        const matchesCommodity = !commodityFilter ? true : (() => {
            const rowCommodity = row["Commodity"];
            if (rowCommodity === null || rowCommodity === undefined) return false;

            const commodityString = String(rowCommodity);
            const filterString = String(commodityFilter);

            // Try multiple comparison formats
            return (
                commodityString === filterString ||
                commodityString.trim() === filterString.trim() ||
                commodityString.toLowerCase() === filterString.toLowerCase() ||
                commodityString.trim().toLowerCase() === filterString.trim().toLowerCase()
            );
        })();

        return matchesSearch && matchesStatus && matchesCommodity;
    });

    // Find the selected status and commodity option objects
    const selectedStatusOption = statusOptions.find(option => option.value === statusFilter) || null;
    const selectedCommodityOption = commodityOptions.find(option => option.value === commodityFilter) || null;

const columns = [
    {
        field: "actions",
        header: "Actions",
        align: "center",
        width: "80px",
        render: (row) => (
            <CBox sx={{ display: "flex", justifyContent: "center" }}>
                <CTooltip title="Edit">
                    <span>
                        <CButton
                            type="icon"
                            customIcon={<EditIcon style={{ color: "#002A5B" }} />}
                            onClick={() => handleEditClick(row)}
                            size="small"
                            iconButton={true}
                            disabled={backdropOpen}
                        />
                    </span>
                </CTooltip>
            </CBox>
        )
    },
    { field: "material_number_index", header: "Material Number", align: "left", minWidth: "120px" },
    { field: "Material Description", header: "Material Description", align: "left", minWidth: "200px" },
    { field: "Commodity", header: "Commodity", align: "left", minWidth: "120px" },
    {
        field: "Status",
        header: "Status",
        align: "center",
        minWidth: "120px",
        render: (row) => {
            const getStatusColor = (status) => {
                const lower = status?.toLowerCase() || '';
                if (lower === "processed") return "success";
                if (lower === "to be reviewed") return "warning";
                if (lower === "not enriched") return "info";
                return "default";
            };

            return (
                <CChip
                    label={row.Status}
                    color={getStatusColor(row.Status)}
                    variant="outlined"
                    size="small"
                    sx={{
                        minWidth: getMaxChipWidth,
                        width: getMaxChipWidth,
                        height: '24px',
                        minHeight: '24px',
                        fontSize: '0.7rem',
                        fontWeight: 500,
                        '& .MuiChip-label': {
                            fontSize: '0.7rem',
                            padding: '0 6px',
                            lineHeight: '1'
                        },
                        '& .MuiChip-root': {
                            height: '24px',
                            fontSize: '0.7rem',
                        }
                    }}
                />
            );
        }
    },
    // ✅ OPTIONAL: Add a reference links indicator column
    {
        field: "reference_links_indicator",
        header: "Links",
        align: "center",
        width: "60px",
        render: (row) => {
            const hasLinks = row["Reference Links"] && 
                            (Array.isArray(row["Reference Links"]) ? 
                             row["Reference Links"].length > 0 : 
                             row["Reference Links"].length > 0);
            
            return hasLinks ? (
                <CTooltip title="Has reference links">
                    <LinkIcon sx={{ color: "#3b82f6", fontSize: 16 }} />
                </CTooltip>
            ) : null;
        }
    }
];

    return (
        <CThemeProvider theme={conasTheme}>
            <CBox
                sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    marginTop: "90px",
                }}
            >
                <CBox
                    sx={{
                        width: "100%",
                        maxWidth: "1920px",
                        px: { xs: 2, sm: 3, md: 4 },
                        boxSizing: "border-box"
                    }}
                >
                    {/* Header with Project Name and Back Button */}
                    <CBox
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            width: "100%",
                        }}
                    >
                        <CText
                            variant="h5"
                            strong={true}
                            sx={{
                                color: conasTheme.palette.primary.main,
                                fontSize: { xs: "0.9rem", sm: "1.18rem" },
                                marginLeft: "30px"
                            }}
                        >
                            Project name: {projectName} ({customerName})
                        </CText>

                        <CButton
                            type="custom"
                            variant="outlined"
                            size="small"
                            color="primary"
                            customIcon={<ArrowBackIcon />}
                            iconPosition="start"
                            onClick={handleBack}
                            label="Back to History"
                            sx={{
                                marginRight: "50px"
                            }}
                            disabled={backdropOpen} // ✅ Disable back button when backdrop is open
                        />
                    </CBox>

                    <CCard
                        cards={[
                            {
                                id: "enrichment-details",
                                header: "Material Enrichment Details",
                                value: "",
                                content: (
                                    <>
                                        {/* Filters Section */}
                                        <CBox
                                            sx={{
                                                display: "flex",
                                                flexDirection: "row",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                mb: 2,
                                                p: 0,
                                                height: {
                                                    xs: "70px",
                                                    md: "70px",
                                                    lg: "50px"
                                                },
                                                backgroundColor: "#f8f9fa",
                                                borderRadius: 1,
                                                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                                                overflowX: "auto",
                                                overflowY: "hidden"
                                            }}
                                        >
                                            <CBox sx={{
                                                display: "flex",
                                                flexDirection: "row",
                                                gap: 1,
                                                alignItems: "center",
                                                ml: 1,
                                                flexShrink: 0
                                            }}>
                                                <CBox sx={{ display: "flex", alignItems: "center" }}>
                                                    <FilterListIcon sx={{ color: conasTheme.palette.primary.main, mr: 0.5, fontSize: "20px" }} />
                                                    <CText variant="subtitle1" sx={{ fontWeight: 600 }}>Filters:</CText>
                                                </CBox>

                                                {/* Status Filter */}
                                                <Autocomplete
                                                    value={selectedStatusOption}
                                                    onChange={handleStatusFilterChange}
                                                    options={statusOptions}
                                                    getOptionLabel={(option) => option.label || ''}
                                                    isOptionEqualToValue={(option, value) => option.value === value?.value}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Status"
                                                            size="small"
                                                            variant="outlined"
                                                            sx={{
                                                                '& .MuiInputBase-root': {
                                                                    height: '32px',
                                                                    fontSize: '0.75rem',
                                                                    padding: '0'
                                                                },
                                                                '& .MuiInputBase-input': {
                                                                    height: '32px',
                                                                    padding: '6px 8px',
                                                                    fontSize: '0.75rem'
                                                                },
                                                                '& .MuiInputLabel-root': {
                                                                    fontSize: '0.75rem',
                                                                    transform: 'translate(14px, 8px) scale(1)',
                                                                    '&.MuiInputLabel-shrink': {
                                                                        transform: 'translate(14px, -6px) scale(0.75)'
                                                                    }
                                                                },
                                                                '& .MuiOutlinedInput-root': {
                                                                    height: '32px'
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                    sx={{
                                                        minWidth: 120,
                                                        width: 200,
                                                        flexShrink: 0
                                                    }}
                                                    clearOnEscape
                                                    disableClearable={false}
                                                    disabled={backdropOpen}
                                                />

                                                {/* Commodity Filter */}
                                                <Autocomplete
                                                    value={selectedCommodityOption}
                                                    onChange={handleCommodityFilterChange}
                                                    options={commodityOptions}
                                                    getOptionLabel={(option) => option.label || ''}
                                                    isOptionEqualToValue={(option, value) => option.value === value?.value}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Commodity"
                                                            size="small"
                                                            variant="outlined"
                                                            sx={{
                                                                '& .MuiInputBase-root': {
                                                                    height: '32px',
                                                                    fontSize: '0.75rem',
                                                                    padding: '0'
                                                                },
                                                                '& .MuiInputBase-input': {
                                                                    height: '32px',
                                                                    padding: '6px 8px',
                                                                    fontSize: '0.75rem'
                                                                },
                                                                '& .MuiInputLabel-root': {
                                                                    fontSize: '0.75rem',
                                                                    transform: 'translate(14px, 8px) scale(1)',
                                                                    '&.MuiInputLabel-shrink': {
                                                                        transform: 'translate(14px, -6px) scale(0.75)'
                                                                    }
                                                                },
                                                                '& .MuiOutlinedInput-root': {
                                                                    height: '32px'
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                    sx={{
                                                        minWidth: 120,
                                                        width: 200,
                                                        flexShrink: 0
                                                    }}
                                                    clearOnEscape
                                                    disableClearable={false}
                                                    disabled={backdropOpen}
                                                />
                                            </CBox>

                                            <CBox sx={{
                                                display: "flex",
                                                flexDirection: "row",
                                                gap: 1,
                                                alignItems: "center",
                                                mr: 1,
                                                ml: 1,
                                                flexShrink: 0
                                            }}>
                                                {/* Search Field */}
                                                <CInput
                                                    placeholder="Search..."
                                                    value={searchTerm}
                                                    onChange={handleSearchChange}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{
                                                        width: 250,
                                                        marginTop: "2px",
                                                        flexShrink: 0,
                                                        height: '32px',
                                                        '& .MuiInputBase-root': {
                                                            height: '32px',
                                                            fontSize: '0.75rem',
                                                            padding: '0'
                                                        },
                                                        '& .MuiInputBase-input': {
                                                            height: '32px',
                                                            padding: '6px 8px',
                                                            fontSize: '0.75rem'
                                                        },
                                                        '& .MuiOutlinedInput-root': {
                                                            height: '32px',
                                                            '& fieldset': {
                                                                borderWidth: '1px'
                                                            }
                                                        }
                                                    }}
                                                    startAdornment={<SearchIcon fontSize="small" />}
                                                    endAdornment={
                                                        searchTerm ? (
                                                            <CButton
                                                                type="icon"
                                                                customIcon={<CloseIcon fontSize="small" />}
                                                                onClick={handleClearSearch}
                                                                size="small"
                                                                iconButton={true}
                                                                disabled={backdropOpen}
                                                            />
                                                        ) : null
                                                    }
                                                    disabled={backdropOpen}
                                                />

                                                {/* Reset All Filters Button */}
                                                <CButton
                                                    type="custom"
                                                    variant="outlined"
                                                    color="secondary"
                                                    customIcon={<RefreshIcon fontSize="small" />}
                                                    iconPosition="start"
                                                    onClick={handleResetFilters}
                                                    label="Reset"
                                                    disabled={!filtersApplied || backdropOpen}
                                                    sx={{
                                                        height: "32px",
                                                        marginTop: "2px",
                                                        flexShrink: 0,
                                                        fontSize: '0.75rem',
                                                        padding: '6px 12px',
                                                        minHeight: '32px'
                                                    }}
                                                />
                                            </CBox>
                                        </CBox>

                                        {/* Table */}
                                        <CBox sx={{ overflowX: "auto", width: "100%" }}>
                                            <CTable
                                                columns={columns}
                                                data={filteredData}
                                                loading={isLoading}
                                                showSearch={false}
                                                page={page}
                                                rowsPerPage={rowsPerPage}
                                                onPageChange={handleChangePage}
                                                onRowsPerPageChange={handleChangeRowsPerPage}
                                                defaultRowsPerPage={10}
                                                rowsPerPageOptions={[5, 10, 25, 50]}
                                                emptyMessage="No records found"
                                                tableProps={{
                                                    size: "small",
                                                    "aria-label": "enrichment details table",
                                                    sx: {
                                                        minWidth: "100%",
                                                        "& .MuiTableCell-root": {
                                                            whiteSpace: "nowrap",
                                                            padding: { xs: "6px 4px", sm: "8px 6px" },
                                                            fontSize: { xs: "0.7rem", sm: "0.8rem" }
                                                        },
                                                        "& .MuiTableCell-head": {
                                                            fontWeight: "bold",
                                                            fontSize: { xs: "0.7rem", sm: "0.8rem" }
                                                        },
                                                        "& .MuiTableRow-root:hover": {
                                                            backgroundColor: "rgba(0, 42, 91, 0.04)"
                                                        }
                                                    }
                                                }}
                                            />
                                        </CBox>
                                    </>
                                )
                            }
                        ]}
                        sx={{
                            p: { xs: 1.5, sm: 2, md: 3 },
                            pt: { xs: 1, sm: 1.5, md: 2 },
                            borderRadius: 2,
                            bgcolor: conasTheme.palette.background.paper,
                            width: "100%",
                            boxSizing: "border-box",
                            maxWidth: "100%",
                        }}
                        fullWidth={true}
                        elevation={3}
                    />
                </CBox>
            </CBox>

            {/* Use the separated Edit Material Dialog component */}
            <EditMaterialDialog
                modalOpen={modalOpen}
                setModalOpen={setModalOpen}
                handleCancelClick={handleCancelClick}
                handleSaveClick={handleSaveClick}
                editData={editData}
                handleFieldChange={handleFieldChange}
                dimensionsArray={dimensionsArray}
                dimensionValues={dimensionValues}
                handleDimensionFieldChange={handleDimensionFieldChange}
                statusOptions={statusOptions}
                commodityOptions={commodityOptions}
                rowId={editData?.id}
            />

            {/* ✅ CBackdrop Component */}
            <CBackDrop
                cOpen={backdropOpen}
                cHandleClose={() => setBackdropOpen(false)}
                cAllowClickToClose={false}
            />

        </CThemeProvider>
    );
};

export default EnrichmentDetailTable;
